import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FrontUrl } from "../../api/FrontUrl";
import { ServerUrl } from '../../api/ServerUrl';
import { Api } from "../../api/Api";
import swal from "sweetalert";

/* ================= SAMPLE FALLBACK ================= */
const SAMPLE_IMAGES = [
  "https://footbalancetech.com/footserver/public/backend/img/product/9B1aC-1716107642-Product.jpg",
];

/* ================= STYLES (UNCHANGED) ================= */
const styles = `
.lp-card { border-radius: 12px; background: #fff; }
.lp-gallery { background: linear-gradient(180deg,#fff,#fbfbfc); }
.lp-main-img { border:1px solid #555555; display:flex; align-items:center; justify-content:center; overflow:hidden; position:relative; border-radius: 8px; background:#f8f9fa; }
.lp-main-img img{ max-width:100%; max-height:100%; object-fit:contain; display:block; }
.lp-thumb { width:70px; height:56px; border-radius:8px; overflow:hidden; cursor:pointer; border:1px solid #ececec; display:flex; align-items:center; justify-content:center; background:#fff }
.lp-thumb img{ width:100%; height:100%; object-fit:cover }

/* Zoom lens */
.zoom-lens {
  position: absolute;
  width: 250px;
  height: 250px;
  border-radius: 8px;
  border: 2px solid rgba(0,0,0,0.12);
  box-shadow: 0 8px 22px rgba(0,0,0,0.12);
  pointer-events: none;
  display: none;
  background-repeat: no-repeat;
  background-position: center;
  z-index: 30;
}


.color-wrap{
  overflow: visible !important;
}

/* show lens when active */
.zoom-lens.show { display: block; }

/* responsive adjustments */
@media (max-width: 992px){ .lp-main-img{ height:380px } .zoom-lens{ width:140px; height:140px } }
@media (max-width: 576px){ .lp-main-img{ height:260px } .lp-thumb{ width:56px; height:44px } .zoom-lens{ width:110px; height:110px } }

.color-swatch{
  width: 50px;
  height: 40px;
  padding:2px;
  box-sizing: border-box;
  border-radius: 8px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.color-swatch.selected{
  border: 3px solid #000;
}

/* small guide buttons */
.small-guide {
  padding:6px 10px;
  border-radius:8px;
  font-size:13px;
  display:inline-flex;
  gap:8px;
  align-items:center;
  border:1px solid #e9ecef;
  background:#fff;
  text-decoration:none;
  color:inherit;
}
.small-guide svg { width:16px; height:16px; }

/* BACKDROP */
.guide-modal-backdrop{
  position:fixed;
  inset:0;
  background:rgba(0,0,0,0);
  display:flex;
  align-items:center;
  justify-content:center;
  transition:background .3s ease;
  z-index:9999;
}

.guide-modal-backdrop.open{
  background:rgba(0,0,0,.6);
}

/* MODAL BOX */
.guide-modal{
  background:#fff;
  width:90%;
  max-width:900px;      /* ✅ width control */
  max-height:90vh;
  border-radius:12px;
  overflow:hidden;
  transform:translateY(40px) scale(.95);
  opacity:0;
  transition:all .3s ease;
}

.guide-modal.open{
  transform:translateY(0) scale(1);
  opacity:1;
}

/* HEADER */
.guide-modal-header{
  position:sticky;
  top:0;
  background:#fff;
  padding:12px 16px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  border-bottom:1px solid #eee;
  z-index:5;
}

.guide-modal-header h5{
  margin:0;
  font-size:16px;
  font-weight:600;
}

.guide-modal-header button{
  background:none;
  border:none;
  font-size:22px;
  cursor:pointer;
}

/* BODY */
.guide-modal-body{
  padding:16px;
  overflow:auto;
  max-height:calc(90vh - 56px); /* header height minus */
  scroll-behavior:smooth;
}

.guide-modal-body img{
  width:100%;
  height:auto;
  display:block;
}
`;

export default function LProductDetails() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { http } = Api();
  /* ================= STATE ================= */
  const [product, setProduct] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [images, setImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [colorIndex, setColorIndex] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const [guideImage, setGuideImage] = useState("");
  const [guideTitle, setGuideTitle] = useState("");
  const mainRef = useRef(null);
  const lensRef = useRef(null);

  const [errorMsg, setErrorMsg] = useState("");

  /* ================= FETCH PRODUCT ================= */
  useEffect(() => {
    http
      .get(`/get-landing-page-product/${slug}`)
      .then(res => {
        if (res.data?.success) {
          setProduct(res.data.data);
        }
      })
      .catch(err => {
        console.error("Product fetch error:", err);
      });
  }, [slug]);

  /* ================= INJECT CSS ================= */
  useEffect(() => {
    if (!document.getElementById("lp-style")) {
      const s = document.createElement("style");
      s.id = "lp-style";
      s.innerHTML = styles;
      document.head.appendChild(s);
    }
  }, []);

  /* ================= DATA MAPPING ================= */
  const title = product?.product_name || "";
  const price = product?.price || 0;
  const short_description = product?.short_description || "Short Description";
  const article_number = product?.article_number || "Article Number";
  const description = product?.description || "";
  const sizeguide = product?.sizeguide || "";
  const careguide = product?.careguide || "";

  const format = (n) =>
    Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const sizes = Array.isArray(product?.size)
    ? product.size.map(s => ({ size: s, available: true }))
    : [];


  const colors = Array.isArray(product?.colors)
    ? product.colors.map(c => ({
      id: c.id,
      name: c.color_name,
      hex: c.color_code,
    }))
    : [];

    console.log('color code is ',colors);   

  const getColorImage = (colorId) => {
    const imgObj = product?.color_imgs?.find(
      (img) => img.colorid === colorId
    );

    return imgObj
      ? `${ServerUrl}${imgObj.image}`
      : null;
  };

  const openGuideModal = (type) => {
    if (type === "care") {
      setGuideImage(`${ServerUrl}${careguide}`);
      setGuideTitle("Care Guide");
    }

    if (type === "size") {
      setGuideImage(`${ServerUrl}${sizeguide}`);
      setGuideTitle("Size Guide");
    }

    setGuideModalOpen(true); 

    setTimeout(() => {
      setGuideVisible(true);
    }, 10);
  };

  const closeGuideModal = () => {
    setGuideVisible(false);

    setTimeout(() => {
      setGuideModalOpen(false);
    }, 300);
  };





  /* ================= INITIAL COLOR LOAD ================= */
  useEffect(() => {
    if (!product) return;

    const imgs =
      product.images?.map(
        img => `${ServerUrl}${img.image}`
      ) || [];

    setAllImages(imgs.length ? imgs : SAMPLE_IMAGES);
    setActiveIndex(0); // 1st image big
  }, [product]);


  /* ================= COLOR CHANGE ================= */
  useEffect(() => {
    if (colorIndex === null || !product || !colors[colorIndex]) return;

    const colorId = colors[colorIndex].id;

    // ওই color এর প্রথম image খুঁজি
    const colorImageIndex = allImages.findIndex((imgUrl) => {
      const img = product.images.find(
        i => `${ServerUrl}${i.image}` === imgUrl
      );
      return img?.color_id === colorId;
    });

    if (colorImageIndex !== -1) {
      setActiveIndex(colorImageIndex); // ✅ শুধু বড় image বদলাবে
    }
  }, [colorIndex]);

  /* ================= HANDLERS ================= */
  const changeSize = (s) => setSelectedSize(s.size);
  const inc = () => setQuantity(q => Math.min(10, q + 1));
  const dec = () => setQuantity(q => Math.max(1, q - 1));

  const buyNow = () => {

    if (colorIndex === null || !colors[colorIndex]) {
      swal("Oops!", "Please select a color", "warning");
      return;
    }

    if (!selectedSize) {
      swal("Oops!", "Please select a size", "warning");
      return;
    }

    if (!quantity || quantity < 1) {
      swal("Oops!", "Please enter quantity", "warning");
      return;
    }

    /* ================= SESSION DATA ================= */
    const orderData = {
      product_id: product.id,
      product_name: title,
      article_number: article_number,
      price: price,
      color: colors[colorIndex],
      size: selectedSize,
      quantity: quantity,
      image: allImages[activeIndex],
    };

    sessionStorage.setItem("checkout_product", JSON.stringify(orderData));

    swal({
      title: "Success!",
      text: "Product added successfully",
      icon: "success",
      timer: 1200,
      buttons: false,
    });

    setTimeout(() => {
      navigate("/checkout");
    }, 1200);
  };


  /* ================= ZOOM ================= */
  const handleMouseMove = (e) => {
    const main = mainRef.current;
    const lens = lensRef.current;
    if (!main || !lens) return;

    const rect = main.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    lens.style.left = `${x - 100}px`;
    lens.style.top = `${y - 100}px`;
    lens.style.backgroundPosition = `${(x / rect.width) * 100}% ${(y / rect.height) * 100}%`;
  };

  const showLens = () => {
    if (!lensRef.current) return;
    lensRef.current.style.backgroundImage = `url("${allImages[activeIndex]}")`;
    lensRef.current.style.backgroundSize = "400%";
    lensRef.current.classList.add("show");
  };

  const hideLens = () => {
    lensRef.current?.classList.remove("show");
  };

  /* ================= JSX (UNCHANGED DESIGN) ================= */
  return (
    <>
      {!product ? (
        <div className="text-center py-5">Loading...</div>
      ) : (
        <>
          <section className="pt-5 mt-3"></section>
          <div className="section">
            <div className="container  ">
              <div className="row lp-card p-3 mt-5 mb-3 gx-0 bg-light">
                {/* Gallery */}
                <div className="col-12 col-lg-6 lp-gallery p-4">
                  <div
                    className="lp-main-img rounded mb-3"
                    role="img"
                    aria-label={title}
                    ref={mainRef}
                    onMouseMove={handleMouseMove}
                    onMouseEnter={showLens}
                    onMouseLeave={hideLens}
                  >

                    <img src={allImages[activeIndex]} alt={`Product image ${activeIndex + 1}`} />
                    <div ref={lensRef} className="zoom-lens" aria-hidden />
                  </div>

                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex gap-2">


                      {allImages.map((src, i) => (
                        <div key={i} className="lp-thumb" onClick={() => setActiveIndex(i)}>
                          <img src={src} />
                        </div>
                      ))}

                    </div>

                    <div className="d-flex gap-2">
                      {/* Prev Button */}
                      <button
                        className="gallery-nav-btn"
                        onClick={() => setActiveIndex(p => Math.max(0, p - 1))}
                        aria-label="Previous image"
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path
                            d="M15 6L9 12L15 18"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      {/* Next Button */}
                      <button
                        className="gallery-nav-btn"
                        onClick={() =>
                          setActiveIndex(p => Math.min(allImages.length - 1, p + 1))
                        }
                        aria-label="Next image"
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path
                            d="M9 6L15 12L9 18"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>


                {/* Product Info */}
                <div className="col-12 col-lg-6 p-4 pt-1">
                  <h2 className="h4 fw-bold mb-2">{title}</h2>
                  <h2 className="h5 fw-semi-bold mb-2"> {article_number} </h2>

                  <div className="mb-3">
                    <div className="h4 "> Price - {format(price)} /=</div>
                    <p>{short_description}</p>
                  </div>



                  <div className="mb-3 flex-column flex-md-row gap-3">
                    <div>
                      <label className="form-label fw-semibold d-block mb-2">Color</label>

                      <div className="d-flex align-items-start gap-3 mb-3 flex-wrap">
                        {colors.map((c, i) => {
                          const colorImg = getColorImage(c.id);

                          return (
                            <div key={i} className="text-center">
                              <button
                                type="button"
                                onClick={() => setColorIndex(i)}
                                className={`btn btn-outline-secondary color-swatch ${
                                  colorIndex === i ? "selected" : ""
                                }`}
                                title={c.color_name}
                                style={{
                                  backgroundImage: colorImg ? `url(${colorImg})` : "none",
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                }}
                                aria-label={`Select color ${c.color_name}`}
                              /> <br/>
                              {c.name}
                            </div>

                          
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="form-label fw-semibold d-block mb-2">Select size</label>
                      <div className="d-flex flex-wrap gap-2">
                        {sizes.map((s, idx) => (
                          <button
                            key={idx}
                            onClick={() => changeSize(s)}
                            className={`btn ${selectedSize === s.size ? 'btn-dark' : 'btn-outline-secondary'} ${!s.available ? 'disabled opacity-50' : ''}`}
                          >{s.size}</button>
                        ))}
                      </div>
                    </div>


                  </div>

                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div>
                      <label className="form-label mb-1">Quantity</label>
                      <div className="input-group" style={{ width: 140 }}>
                        <button className="btn btn-outline-secondary" onClick={dec} aria-label="Decrease">−</button>
                        <input type="text" className="form-control text-center" value={quantity} readOnly aria-label="Quantity" />
                        <button className="btn btn-outline-secondary" onClick={inc} aria-label="Increase">+</button>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mb-4">
                    <button
                      className="btn btn-dark mt-2 px-5 py-2"
                      onClick={buyNow}
                    >
                      Proceed your order
                    </button>
                  </div>

                  {/* <div className="d-flex gap-2 mb-4">
                  <Link to="/checkout" className="btn btn-dark px-5 py-2" onClick={buyNow} disabled={!selectedSize} title={!selectedSize ? 'Choose size first' : 'Buy now'}>Order Now </Link>
                </div> */}

                  <div className="d-flex gap-2 mb-3 align-items-center">
                    <button
                      type="button"
                      className="small-guide me-2"
                      title="Care Guide"
                      onClick={() => openGuideModal("care")}
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M3 7h18" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M8 7v10a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V7" stroke="currentColor" strokeWidth="1.6" />
                      </svg>
                      <span>Care Guide</span>
                    </button>

                    <button
                      type="button"
                      className="small-guide"
                      title="Size Guide"
                      onClick={() => openGuideModal("size")}
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M12 2v20M2 12h20" stroke="currentColor" strokeWidth="1.6" />
                      </svg>
                      <span>Size Guide</span>
                    </button>
                  </div>



                  {/* <div className="text-muted small">
                  <p className="mb-1"><strong>Delivery:</strong> Free on orders over ৳ 2000</p>
                  <p className="mb-1"><strong>Returns:</strong> 7 days easy return</p>
                </div> */}
                </div>
              </div>
            </div>

            <div className="product-details-full mb-5">
              <div className="container">
                <div className="row">
                  <div className="col-12 col-lg-12">
                    <h3 className="mb-3 fw-bold text-dark">Product Details</h3>
                    <div
                      className="product-description"
                      dangerouslySetInnerHTML={{
                        __html: product?.description || "<p>Product Details</p>",
                      }}
                    />
                  </div>


                </div>
              </div>
            </div>


            {guideModalOpen && (
              <div
                className={`guide-modal-backdrop ${guideVisible ? "open" : ""}`}
                onClick={closeGuideModal}
              >
                <div
                  className={`guide-modal ${guideVisible ? "open" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* HEADER */}
                  <div className="guide-modal-header">
                    <h5>{guideTitle}</h5>
                    <button onClick={closeGuideModal}>×</button>
                  </div>

                  {/* BODY */}
                  <div className="guide-modal-body">
                    <img src={guideImage} alt={guideTitle} />
                  </div>
                </div>
              </div>

            )}
          </div>
        </>
      )}
    </>
  );
}
